import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Plus, 
  Edit, 
  Trash2, 
  Users, 
  Check, 
  X,
  ToggleLeft,
  ToggleRight,
  Save,
  ArrowLeft
} from 'lucide-react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAdminTheme, getThemeClasses } from '../contexts/AdminTheme';
import AdminThemeSwitcher from './AdminThemeSwitcher';

const AdminPlanManager = () => {
  const { theme } = useAdminTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPlan, setEditingPlan] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    features: [''],
    limits: {
      maxSites: 1,
      maxPagesPerSite: 1,
      maxSectionsPerPage: 6,
      maxLanguages: 1,
      unlimitedSites: false,
      unlimitedPages: false,
      unlimitedSections: false,
      unlimitedLanguages: false
    },
    pricing: {
      monthly: 0,
      yearly: 0,
      currency: 'USD'
    },
    isActive: true,
    sortOrder: 1
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/admin/plans', {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      } else {
        toast.error('Failed to load plans');
      }
    } catch (error) {
      console.error('Failed to load plans:', error);
      toast.error('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingPlan 
        ? `/api/admin/plans/${editingPlan}`
        : '/api/admin/plans';
        
      const method = editingPlan ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...formData,
          features: formData.features.filter(f => f.trim() !== '')
        })
      });

      if (response.ok) {
        toast.success(`Plan ${editingPlan ? 'updated' : 'created'} successfully`);
        setShowCreateForm(false);
        setEditingPlan(null);
        resetForm();
        loadPlans();
      } else {
        const error = await response.json();
        toast.error(error.error || `Failed to ${editingPlan ? 'update' : 'create'} plan`);
      }
    } catch (error) {
      toast.error(`Failed to ${editingPlan ? 'update' : 'create'} plan`);
    }
  };

  const handleEdit = (plan) => {
    setFormData({
      name: plan.name,
      slug: plan.slug,
      description: plan.description,
      features: plan.features.length > 0 ? plan.features : [''],
      limits: plan.limits,
      pricing: plan.pricing,
      isActive: plan.isActive,
      sortOrder: plan.sortOrder
    });
    setEditingPlan(plan._id);
    setShowCreateForm(true);
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to delete this plan? Users currently using this plan will be affected.')) {
      return;
    }

    try {
      const response = await fetch(`/api/admin/plans/${planId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        toast.success('Plan deleted successfully');
        loadPlans();
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete plan');
      }
    } catch (error) {
      toast.error('Failed to delete plan');
    }
  };

  const togglePlanStatus = async (planId) => {
    try {
      const response = await fetch(`/api/admin/plans/${planId}/toggle`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        loadPlans();
      } else {
        toast.error('Failed to toggle plan status');
      }
    } catch (error) {
      toast.error('Failed to toggle plan status');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      features: [''],
      limits: {
        maxSites: 1,
        maxPagesPerSite: 1,
        maxSectionsPerPage: 6,
        maxLanguages: 1,
        unlimitedSites: false,
        unlimitedPages: false,
        unlimitedSections: false,
        unlimitedLanguages: false
      },
      pricing: {
        monthly: 0,
        yearly: 0,
        currency: 'USD'
      },
      isActive: true,
      sortOrder: 1
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const addFeature = () => {
    setFormData({ 
      ...formData, 
      features: [...formData.features, ''] 
    });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${themeClasses.page}`}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className={`mt-4 ${themeClasses.text}`}>Loading plans...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={themeClasses.page}>
      {/* Header */}
      <header className={themeClasses.header}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin"
                className={themeClasses.iconButton}
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className={`text-2xl ${themeClasses.heading}`}>Plan Manager</h1>
                <p className={themeClasses.text}>Manage subscription plans and limits</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <AdminThemeSwitcher />
              <button
                onClick={() => {
                  resetForm();
                  setShowCreateForm(true);
                  setEditingPlan(null);
                }}
                className={themeClasses.button}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Plan
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {showCreateForm ? (
          // Create/Edit Form
          <div className={`${themeClasses.card} p-6`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-lg font-semibold ${themeClasses.heading}`}>
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingPlan(null);
                  resetForm();
                }}
                className={themeClasses.iconButton}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Basic Info */}
                <div>
                  <label className={themeClasses.label}>
                    Plan Name
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full ${themeClasses.input}`}
                    required
                  />
                </div>

                <div>
                  <label className={themeClasses.label}>
                    Slug (URL-friendly name)
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                    className={`w-full ${themeClasses.input}`}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={themeClasses.label}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className={`w-full ${themeClasses.input}`}
                  rows="3"
                  required
                />
              </div>

              {/* Features */}
              <div>
                <label className={themeClasses.label}>
                  Features
                </label>
                {formData.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-2 mb-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => updateFeature(index, e.target.value)}
                      className={`flex-1 ${themeClasses.input}`}
                      placeholder="Enter feature description"
                    />
                    {formData.features.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFeature(index)}
                        className="p-2 text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addFeature}
                  className={`${themeClasses.textMuted} ${themeClasses.hover} text-sm`}
                >
                  + Add Feature
                </button>
              </div>

              {/* Limits */}
              <div className="border-t pt-6">
                <h4 className={`text-md font-semibold ${themeClasses.heading} mb-4`}>Plan Limits</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={themeClasses.label}>
                      Max Sites
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="number"
                        value={formData.limits.maxSites}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, maxSites: parseInt(e.target.value) || 0 }
                        })}
                        className={`w-32 ${themeClasses.input}`}
                        disabled={formData.limits.unlimitedSites}
                        min="0"
                      />
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.limits.unlimitedSites}
                          onChange={(e) => setFormData({
                            ...formData,
                            limits: { ...formData.limits, unlimitedSites: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        Unlimited
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Max Pages per Site
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="number"
                        value={formData.limits.maxPagesPerSite}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, maxPagesPerSite: parseInt(e.target.value) || 0 }
                        })}
                        className={`w-32 ${themeClasses.input}`}
                        disabled={formData.limits.unlimitedPages}
                        min="0"
                      />
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.limits.unlimitedPages}
                          onChange={(e) => setFormData({
                            ...formData,
                            limits: { ...formData.limits, unlimitedPages: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        Unlimited
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Max Sections per Page
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="number"
                        value={formData.limits.maxSectionsPerPage}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, maxSectionsPerPage: parseInt(e.target.value) || 0 }
                        })}
                        className={`w-32 ${themeClasses.input}`}
                        disabled={formData.limits.unlimitedSections}
                        min="0"
                      />
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.limits.unlimitedSections}
                          onChange={(e) => setFormData({
                            ...formData,
                            limits: { ...formData.limits, unlimitedSections: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        Unlimited
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Max Languages per Site
                    </label>
                    <div className="flex items-center space-x-4">
                      <input
                        type="number"
                        value={formData.limits.maxLanguages}
                        onChange={(e) => setFormData({
                          ...formData,
                          limits: { ...formData.limits, maxLanguages: parseInt(e.target.value) || 1 }
                        })}
                        className={`w-32 ${themeClasses.input}`}
                        disabled={formData.limits.unlimitedLanguages}
                        min="1"
                      />
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.limits.unlimitedLanguages}
                          onChange={(e) => setFormData({
                            ...formData,
                            limits: { ...formData.limits, unlimitedLanguages: e.target.checked }
                          })}
                          className="mr-2"
                        />
                        Unlimited
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Sort Order
                    </label>
                    <input
                      type="number"
                      value={formData.sortOrder}
                      onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                      className={`w-32 ${themeClasses.input}`}
                      min="0"
                    />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className="border-t pt-6">
                <h4 className={`text-md font-semibold ${themeClasses.heading} mb-4`}>Pricing</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className={themeClasses.label}>
                      Monthly Price
                    </label>
                    <input
                      type="number"
                      value={formData.pricing.monthly}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, monthly: parseFloat(e.target.value) || 0 }
                      })}
                      className={`w-full ${themeClasses.input}`}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Yearly Price
                    </label>
                    <input
                      type="number"
                      value={formData.pricing.yearly}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, yearly: parseFloat(e.target.value) || 0 }
                      })}
                      className={`w-full ${themeClasses.input}`}
                      min="0"
                      step="0.01"
                    />
                  </div>

                  <div>
                    <label className={themeClasses.label}>
                      Currency
                    </label>
                    <select
                      value={formData.pricing.currency}
                      onChange={(e) => setFormData({
                        ...formData,
                        pricing: { ...formData.pricing, currency: e.target.value }
                      })}
                      className={`w-full ${themeClasses.input}`}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Status */}
              <div className="border-t pt-6">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="mr-2"
                  />
                  <span className={`text-sm font-medium ${themeClasses.text}`}>Plan is active</span>
                </label>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setEditingPlan(null);
                    resetForm();
                  }}
                  className={themeClasses.buttonSecondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={themeClasses.button}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        ) : (
          // Plans List
          <div className="space-y-6">
            {plans.map(plan => (
              <div key={plan._id} className={`${themeClasses.card} p-6`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-4 mb-2">
                      <h3 className={`text-xl font-semibold ${themeClasses.heading}`}>{plan.name}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        plan.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {plan.isActive ? 'Active' : 'Inactive'}
                      </span>
                      {plan.pricing.monthly > 0 && (
                        <span className={`text-lg font-bold ${themeClasses.text}`}>
                          ${plan.pricing.monthly}/mo
                        </span>
                      )}
                    </div>
                    <p className={`${themeClasses.text} mb-4`}>{plan.description}</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-4">
                      <div className={`${themeClasses.secondary} p-2 rounded-md`}>
                        <div className={`text-sm ${themeClasses.textMuted}`}>Sites</div>
                        <div className="font-semibold">
                          {plan.limits.unlimitedSites ? 'Unlimited' : plan.limits.maxSites}
                        </div>
                      </div>
                      <div className={`${themeClasses.secondary} p-2 rounded-md`}>
                        <div className={`text-sm ${themeClasses.textMuted}`}>Pages/Site</div>
                        <div className="font-semibold">
                          {plan.limits.unlimitedPages ? 'Unlimited' : plan.limits.maxPagesPerSite}
                        </div>
                      </div>
                      <div className={`${themeClasses.secondary} p-2 rounded-md`}>
                        <div className={`text-sm ${themeClasses.textMuted}`}>Sections/Page</div>
                        <div className="font-semibold">
                          {plan.limits.unlimitedSections ? 'Unlimited' : plan.limits.maxSectionsPerPage}
                        </div>
                      </div>
                      <div className={`${themeClasses.secondary} p-2 rounded-md`}>
                        <div className={`text-sm ${themeClasses.textMuted}`}>Languages/Site</div>
                        <div className="font-semibold">
                          {plan.limits.unlimitedLanguages ? 'Unlimited' : plan.limits.maxLanguages}
                        </div>
                      </div>
                      <div className={`${themeClasses.secondary} p-2 rounded-md`}>
                        <div className={`text-sm ${themeClasses.textMuted}`}>Sort Order</div>
                        <div className="font-semibold">{plan.sortOrder}</div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <div className={`text-sm font-medium ${themeClasses.text} mb-2`}>Features:</div>
                      <ul className={`list-disc list-inside text-sm ${themeClasses.text} space-y-1`}>
                        {plan.features.map((feature, index) => (
                          <li key={index}>{feature}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => togglePlanStatus(plan._id)}
                      className={`p-2 rounded-lg transition ${
                        plan.isActive ? `text-green-400 ${themeClasses.hover}` : `text-red-400 ${themeClasses.hover}`
                      }`}
                      title={plan.isActive ? 'Deactivate plan' : 'Activate plan'}
                    >
                      {plan.isActive ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={() => handleEdit(plan)}
                      className={`p-2 ${themeClasses.textMuted} ${themeClasses.hover} rounded-lg transition`}
                      title="Edit plan"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(plan._id)}
                      className={`p-2 text-red-400 ${themeClasses.hover} rounded-lg transition`}
                      title="Delete plan"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {plans.length === 0 && (
              <div className="text-center py-12">
                <Settings className={`w-12 h-12 ${themeClasses.textMuted} mx-auto mb-4`} />
                <h3 className={`text-lg font-semibold ${themeClasses.heading} mb-2`}>No plans created yet</h3>
                <p className={`${themeClasses.text} mb-4`}>Create your first subscription plan to get started.</p>
                <button
                  onClick={() => {
                    resetForm();
                    setShowCreateForm(true);
                    setEditingPlan(null);
                  }}
                  className={`${themeClasses.button} flex items-center mx-auto`}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Plan
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPlanManager;